
/**
 * Interface for the schedule item provided to the service
 */
export interface ScheduledTaskItem {
    taskId: string;
    date: string; // YYYY-MM-DD
    assignedTo?: string;
}

/**
 * Interface for the task updater function
 * This allows the service to be decoupled from the specific database implementation
 */
export type TaskUpdater = (taskId: string, updateData: any) => Promise<{ success: boolean; id: string } | undefined>;

/**
 * Service to handle rescheduling of assurance tasks
 */
export class AssuranceScheduler {
    /**
     * Applies the given schedule to the assurance tasks using a callback
     * @param schedule List of scheduled tasks with their new dates
     * @param updateTask Function to update a single task
     * @returns Promise that resolves when all updates are complete
     */
    static async applySchedule(
        schedule: ScheduledTaskItem[],
        updateTask: TaskUpdater
    ): Promise<{ success: number; failed: number }> {
        let successCount = 0;
        let failedCount = 0;

        const updatePromises = schedule.map(async (item) => {
            try {
                const newDate = new Date(item.date);
                const updateData: any = {
                    checkFrequency: {
                        nextCheck: newDate
                    }
                };
                
                if (item.assignedTo) {
                    updateData.checkDetail = {
                        assignedTo: item.assignedTo
                    };
                }

                const result = await updateTask(item.taskId, updateData);
                
                if (result?.success) {
                    successCount++;
                } else {
                    failedCount++;
                    console.error(`Failed to update task ${item.taskId}`);
                }
            } catch (error) {
                failedCount++;
                console.error(`Error updating task ${item.taskId}:`, error);
            }
        });

        await Promise.all(updatePromises);

        return { success: successCount, failed: failedCount };
    }

    /**
     * Applies the schedule directly to Firestore (Supports both Client and Admin SDKs)
     * @param schedule List of scheduled tasks
     * @param companyID The ID of the company/tenant
     * @param db The Firestore database instance
     * @param timestampConstructor Optional constructor for Firestore Timestamp (e.g. Timestamp.fromDate). If not provided, uses native Date.
     */
    static async applyScheduleToFirestore(
        schedule: ScheduledTaskItem[],
        companyID: string,
        db: any,
        timestampConstructor?: (date: Date) => any
    ): Promise<{ success: number; failed: number }> {
        let successCount = 0;
        let failedCount = 0;

        try {
            // We are updating fields in a single document: customers/{companyID}/compliance/assurance
            const docRef = db.collection('customers').doc(companyID).collection('compliance').doc('assurance');
            
            // We can use a batch or a single update call if we map all changes
            // Since we are updating nested fields in a map, we can construct a single update object
            // key: `taskId.checkFrequency.nextCheck`
            
            const updatePayload: Record<string, any> = {};
            
            schedule.forEach(item => {
                const newDate = new Date(item.date);
                const timestamp = timestampConstructor ? timestampConstructor(newDate) : newDate;
                
                // Construct dot notation path for nested update
                // Note: If multiple items exist for the same task, the last one wins for nextCheck.
                // This might need to be revisited if we want to set 'nextCheck' to the earliest date.
                updatePayload[`${item.taskId}.checkFrequency.nextCheck`] = timestamp;

                if (item.assignedTo) {
                    updatePayload[`${item.taskId}.checkDetail.assignedTo`] = item.assignedTo;
                }
            });

            if (Object.keys(updatePayload).length === 0) {
                return { success: 0, failed: 0 };
            }

            // Perform the update
            await docRef.update(updatePayload);
            
            successCount = schedule.length;
        } catch (error) {
            console.error("Failed to apply schedule to Firestore", error);
            // If the bulk update fails, they all fail
            failedCount = schedule.length;
        }

        return { success: successCount, failed: failedCount };
    }
}
